import React, { useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useAuthPrompt } from '../../contexts/AuthPromptContext';
import { useRegisterBottomChrome } from '../../contexts/BottomChromeContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { FILLS, FONTS, GLASS, LAYERS, SIZING } from '../../styles/tokens';
import { isPlainGuestLinkClick } from '../../utils/postLoginRedirect';
import { BrowseGlyphProps, CompassGlyph, DeskBellGlyph, PersonGlyph } from '../icons/browseGlyphs';

type TabId = 'discover' | 'concierge' | 'account';

interface Tab {
  id: TabId;
  label: string;
  path: string;
  Glyph: React.FC<BrowseGlyphProps>;
}

const ICON_SIZE = 22;

const DISCOVER_TAB: Tab = {
  id: 'discover',
  label: 'Discover',
  path: '/discover',
  Glyph: CompassGlyph,
};
const CONCIERGE_TAB: Tab = {
  id: 'concierge',
  label: 'Concierge',
  path: '/concierge-enquiry',
  Glyph: DeskBellGlyph,
};
const LOG_IN_TAB: Tab = { id: 'account', label: 'Log in', path: '/login', Glyph: PersonGlyph };
const PROFILE_TAB: Tab = { id: 'account', label: 'Profile', path: '/profile', Glyph: PersonGlyph };

// Shared per-tab link style. Centralised so the three call sites stay in sync.
const tabButtonStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  padding: '4px 0',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontFamily: FONTS.body,
  textDecoration: 'none',
};

const DISCOVER_PREFIXES = [
  '/discover',
  '/accommodations',
  '/services',
  '/packages',
  '/search',
  '/property',
  '/service',
] as const;

const ACCOUNT_PREFIXES = [
  '/login',
  '/bookings',
  '/wishlist',
  '/profile',
  '/support',
  '/requests',
  '/register',
  '/forgot-password',
  '/verify-email',
] as const;

/**
 * Determines which tab is active for a given pathname.
 * - Discover: `/` and every browse route (stays, stay search results,
 *   services, packages and their detail pages).
 * - Concierge: the enquiry form.
 * - Account (Log in / Profile): login and the account pages reached from it,
 *   plus the auth flow pages.
 * Anything else (cart, checkout) lights no tab.
 */
const activeTabFor = (pathname: string): TabId | null => {
  if (pathname === '/' || DISCOVER_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return 'discover';
  }
  if (pathname.startsWith('/concierge-enquiry')) return 'concierge';
  if (ACCOUNT_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return 'account';
  return null;
};

/**
 * Mobile-only bottom tab bar — three tabs (Discover / Concierge / Log in,
 * or Profile when signed in). Self-gates on `useIsMobile()`; renders nothing on desktop. Opaque
 * page fill, matching the mobile app bar.
 */
const MobileBottomNav: React.FC = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { isAuthenticated } = useAuth();
  const { openAuthPrompt } = useAuthPrompt();
  const navRef = useRef<HTMLElement>(null);
  useRegisterBottomChrome(navRef);

  if (!isMobile) return null;

  const active = activeTabFor(location.pathname);
  const tabs = [DISCOVER_TAB, CONCIERGE_TAB, isAuthenticated ? PROFILE_TAB : LOG_IN_TAB];

  // A plain click on the guest Log in tab opens the pop-up; the link stays for modified clicks and auth pages.
  const handleTabClick = (event: React.MouseEvent<HTMLAnchorElement>, tab: Tab) => {
    if (tab !== LOG_IN_TAB || !isPlainGuestLinkClick(event, location.pathname)) return;
    event.preventDefault();
    openAuthPrompt({ intent: 'signIn' });
  };

  return (
    <nav
      ref={navRef}
      role="navigation"
      aria-label="Mobile navigation"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: LAYERS.mobileBottomNav,
        minHeight: SIZING.bottomNavHeight,
        boxSizing: 'border-box',
        // Opaque, no blur: iOS Safari tints its toolbar only from an opaque fixed edge bar.
        background: FILLS.page,
        borderTop: GLASS.nav.border,
        paddingTop: 8,
        paddingBottom: 'calc(18px + env(safe-area-inset-bottom))',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'flex-end',
      }}
    >
      {tabs.map((tab) => {
        const isActive = active === tab.id;
        return (
          <Link
            key={tab.id}
            to={tab.path}
            aria-label={tab.label}
            aria-current={isActive ? 'page' : undefined}
            onClick={(event) => handleTabClick(event, tab)}
            style={tabButtonStyle}
          >
            <tab.Glyph size={ICON_SIZE} color={isActive ? FILLS.gold : FILLS.textMuted} />
            <span
              style={{
                fontSize: 11,
                letterSpacing: 0.3,
                color: isActive ? FILLS.gold : FILLS.textMuted,
                fontWeight: isActive ? 600 : 500,
              }}
            >
              {tab.label}
            </span>
          </Link>
        );
      })}
    </nav>
  );
};

export default MobileBottomNav;
